import { useStacklineTheme } from '@stackline/ui';
import React, { useEffect, useRef, useState } from 'react';
import {
  BeaconProSidebarFilters,
  ICON_STYLES
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import useSuggestions from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/useSuggestions';
import { fetchBrandNames } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/utils';
import { getAppName } from 'src/utils/app';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { SlExcludeSelectedCheckbox, SlSelectedCheckbox } from 'src/components/SvgIcons';
import { useDebouncedValue } from 'src/utils/Hooks';

const AtlasV2BrandSidebarFilter = (props: GenericSidebarFilterProps) => {
  const theme = useStacklineTheme();
  const appName = getAppName();
  const retailerId = useRetailerId();
  const [preSelectLoading, setPreSelectLoading] = useState(false);
  const { formData, updateSavedSearch } = useSavedSearchBuilder();
  /**
   * Toggle mode for include/exclude; determines which icon to render in the renderItems function.
   */
  const isExcluded = formData.termFilters?.excludedBrandId.values.length > 0;
  const [toggleMode, setToggleMode] = React.useState<'include' | 'exclude'>(isExcluded ? 'exclude' : 'include');
  const brandIdNameRef = useRef<Record<string, string>>({});
  const [selectedBrands, setSelectedBrands] = useState<Array<{ i: string; n?: string }>>(
    formData.termFilters?.brandId.values.length
      ? formData.termFilters?.brandId.values
      : formData.termFilters?.excludedBrandId.values.length
      ? formData.termFilters?.excludedBrandId.values
      : []
  );

  /**
   * Search input for filtering down brands
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');
  const [brandOptions, setBrandOptions] = React.useState<{ brandName: string; brandId: number; selected: boolean }[]>(
    []
  );
  const debouncedInput = useDebouncedValue(currentInput);

  const suggestedBrandOptions = brandOptions
    .filter((brand) => brand?.id !== 0)
    .map((brand) => ({
      ...brand,
      selected: selectedBrands.some((selectedBrand) => Number(selectedBrand.id) === Number(brand.id))
    }));

  const fetchBrandSuggestions = useSuggestions();

  const fetchInitialBrandOptions = async (input: string) => {
    const initialBrandOptions = await fetchBrandSuggestions(input); // Await the async function
    setBrandOptions(initialBrandOptions || []); // Set the options (default to empty array if undefined)
  };

  // Design wants to render first suggestion by alphabetical order
  useEffect(() => {
    fetchInitialBrandOptions('a');
  }, []);

  useEffect(() => {
    const fetchBrandNamesForSelectedBrands = async (brandIds: (string | number)[]) => {
      try {
        const brandNamePromises = brandIds.map((brandId) =>
          fetchBrandNames(['brandId', { appName, retailerId, brandId }])
        );
        const brandNames = await Promise.all(brandNamePromises);
        return brandNames;
      } catch (error) {
        console.error('Error fetching brand names:', error);
        return [];
      }
    };

    const fetchAndSetBrands = async () => {
      setPreSelectLoading(true);
      const brandIds = selectedBrands.map((brand) => brand.i);
      const selectedBrandEntity = (await fetchBrandNamesForSelectedBrands(brandIds)) as Array<{
        i: string;
        n?: string;
      }>;

      setSelectedBrands(selectedBrandEntity);
      setPreSelectLoading(false);
    };

    fetchAndSetBrands();
  }, [appName, retailerId]);

  const getIcon = React.useCallback(() => {
    switch (toggleMode) {
      case 'include': {
        return <SlSelectedCheckbox style={ICON_STYLES} />;
      }

      case 'exclude': {
        return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
      }
      default:
        return null;
    }
  }, [toggleMode]);

  /**
   * Handles the selection of a brand.
   */
  const handleItemSelect = React.useCallback(
    ({ value }: { value: { id: string; name: string; selected: boolean } }) => {
      brandIdNameRef.current = {
        ...brandIdNameRef.current,
        [value.id]: value.name
      };

      if (value.selected) {
        setSelectedBrands((prev) => prev.filter((selectedBrand) => selectedBrand.i !== +value.id));
      } else {
        setSelectedBrands((prev) => [...prev, { n: value.name, i: +value.id }]);
      }

      if (toggleMode === 'include') {
        updateSavedSearch({
          key: BeaconProSidebarFilters.Brand.filterKey,
          newValue: value.id,
          operation: FILTER_OPERATION.Toggle
        });
      } else {
        updateSavedSearch({
          key: BeaconProSidebarFilters.ExcludedBrand.filterKey,
          newValue: value.id,
          operation: FILTER_OPERATION.Toggle
        });
      }
    },
    [selectedBrands, toggleMode, updateSavedSearch]
  );

  /**
   * Renders the list of items as checkboxes.
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {!preSelectLoading &&
          selectedBrands.map(({ i, n }) => {
            return (
              <CheckboxItem
                onClick={() => handleItemSelect({ value: { id: `${i}`, name: n, selected: true } })}
                value={i}
                key={i}
                label={n}
                theme={theme}
                checked={!!selectedBrands.find((brand) => brand.i === i)}
                checkedIcon={getIcon()}
              />
            );
          })}
        {suggestedBrandOptions
          ?.filter(
            (brand) =>
              !selectedBrands.map((selectedBrand) => selectedBrand.i).includes(brand?.brandId)
          )
          .map((brand, index) => (
            <CheckboxItem
              onClick={() => handleItemSelect({ value: { id: brand.id, name: brand.name, selected: false } })}
              value={brand.id}
              key={index}
              label={brand.name}
              theme={theme}
              checked={brand.selected}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [preSelectLoading, selectedBrands, suggestedBrandOptions, theme, getIcon, handleItemSelect]);

  /**
   * Handles the change of the input to filter down the brands.
   */

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  /**
   * Clears the selected brands
   */
  const handleClear = () => {
    updateSavedSearch({
      key: BeaconProSidebarFilters.Brand.filterKey,
      callback: (form) => {
        form.termFilters[BeaconProSidebarFilters.Brand.filterKey].values = [];
        form.termFilters[BeaconProSidebarFilters.ExcludedBrand.filterKey].values = [];
        return form;
      },
      operation: null,
      newValue: null
    });
    setSelectedBrands([]);
    setCurrentInput('');
  };

  const handleToggle = (toggle: 'include' | 'exclude') => {
    setToggleMode(toggle);
    if (toggle === 'include') {
      updateSavedSearch({
        key: BeaconProSidebarFilters.Brand.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.Brand.filterKey].values = selectedBrands.map((brand) => ({
            i: brand.i,
            n: brand.n
          }));
          form.termFilters[BeaconProSidebarFilters.ExcludedBrand.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    } else {
      updateSavedSearch({
        key: BeaconProSidebarFilters.ExcludedBrand.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.ExcludedBrand.filterKey].values = selectedBrands.map((brand) => ({
            i: brand.i,
            n: brand.n
          }));
          form.termFilters[BeaconProSidebarFilters.Brand.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    }
  };

  useEffect(() => {
    fetchInitialBrandOptions(debouncedInput || 'a');
  }, [debouncedInput]);

  return (
    <GenericSidebarFilter
      {...props}
      toggleMode={toggleMode}
      renderItems={renderItems}
      handleClear={handleClear}
      handleToggle={handleToggle}
      includeClear={selectedBrands?.length > 0}
      sidebarInputProps={{
        ...props.sidebarInputProps,
        value: currentInput,
        onChange: handleChange
      }}
    />
  );
};

export default AtlasV2BrandSidebarFilter;
